import React from "react"
import styled from "@emotion/styled"
import { keyframes, css } from "@emotion/react"
import Section from "src/components/Section"
import Image from "src/components/Image"
import ResponsiveComponent from "src/components/ResponsiveComponent"
import Grid, { Container } from "src/components/Grid"
import { mq, util, globals, typography } from "src/styles"

export const tickerAnimation = keyframes`
  0% {
    transform: translate3d(0%, 0, 0);
  }
  100% {
    transform: translate3d(-100%, 0, 0);
  }
`

const Wrapper = styled(Section)`
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  ${({ alignment }) =>
    alignment === "left"
      ? `
    text-align: left;
  `
      : `
    text-align: center;
  `}
  ${({ fullWidth, layoutSize }) =>
    fullWidth &&
    layoutSize === "fullWidth" &&
    `
      padding-top: 40px;
      padding-bottom: 40px;
    `}
  ${({ fullWidth }) =>
    fullWidth
      ? `
    margin-bottom: calc(var(--site-margins) / 2);

  `
      : ""}
  ${({ vertical }) =>
    `
    ${globals.verticalSpacing("padding-top", vertical)}
    ${globals.verticalSpacing("padding-bottom", vertical)}
  `}
  ${({ horizontal }) =>
    `
    ${globals.verticalSpacing("padding-left", horizontal)}
    ${globals.verticalSpacing("padding-right", horizontal)}
  `}
  ${mq.mediumAndBelow} {
    ${({ hideMobile }) => (hideMobile && "display: none")}
  }
  ${mq.largeAndUp} {
    ${({ hideDesktop }) => (hideDesktop && "display: none")}
  }
`

const Inner = styled.div``

// const Headline = styled.h6`
//   ${({ headlineSize }) =>
//     headlineSize === "small"
//       ? `
//     margin: 0;
//     color: var(--light-text-color);
//     margin-bottom: 2em;
//   `
//       : `
//     ${typography.h3}
//     margin: 0;
//     ${globals.verticalSpacing("margin-bottom", 0.5)}
//     color: var(--text-color);
//   `}
// `

const Headline = styled.h6`
  ${({ headlineSize }) =>
    headlineSize === "small"
      ? `
    margin: 0;
    color: var(--light-text-color);
    margin-bottom: 2em;
  `
      : `
    margin: 0;
    ${globals.verticalSpacing("margin-bottom", 0.5)}
    color: var(--text-color);
  `}
`

const LogoTickerWrapper = styled.div`
  display: flex;
  overflow: hidden;
  flex-wrap: nowrap;
  transform-style: preserve-3d;
`

const TickerContent = styled.div`
  display: flex;
  flex-shrink: 0;
  width: 500%;
  ${mq.mediumAndUp} {
    width: 400%;
  }
  ${mq.largeAndUp} {
    width: 350%;
  }
  ${mq.largerAndUp} {
    width: 275%;
  }
  ${mq.extraLargeAndUp} {
    width: 200%;
  }
`

const LogoItems = styled.div`
  display: flex;
  flex-shrink: 0;
  width: 50%;
  align-items: center;
  justify-content: space-around;
  transform-style: preserve-3d;
  transform: translate3d(0, 0, 0);
  animation-fill-mode: forwards;
  animation: ${tickerAnimation} 20s linear infinite;
`

const LogoItem = styled.div`
  ${util.responsiveStyles("padding-left", 40, 30, 20, 10)}
  ${util.responsiveStyles("padding-right", 40, 30, 20, 10)}
  flex-basis: 175px;

  ${mq.mediumAndUp} {
    flex-basis: 250px;
  }
  > div {
    display: flex;
  }
`

const LogoGrid = styled.div`
  display: flex;
  justify-content: ${({ alignment }) =>
    alignment === "left" ? "flex-start" : "center"};
  align-items: center;
  flex-wrap: wrap;
  width: auto;
  ${util.responsiveStyles("margin-left", -80, -50, -40, -32)}
  ${util.responsiveStyles("margin-right", -80, -50, -40, -32)}
  ${util.responsiveStyles("margin-bottom", -80, -50, -40, -32)}
  ${LogoItem} {
    ${util.responsiveStyles("padding-left", 80, 50, 40, 32)}
    ${util.responsiveStyles("padding-right", 80, 50, 40, 32)}
    ${util.responsiveStyles("padding-bottom", 80, 50, 40, 32)}
    width: 50%;
    flex-basis: unset;
    ${mq.mediumAndUp} {
      width: 33.333%;
    }
    ${mq.largeAndUp} {
      width: 25%;
    }
    ${mq.largerAndUp} {
      width: ${100 / 6}%;
    }
  }
`

const LogoTicker = ({
  className,
  nextTheme,
  prevTheme,
  theme,
  spacing,
  isFirstSection,
  headingSize,
  headline,
  headlineSize = "small",
  alignment,
  logos,
  type,
  layoutSize,
  id,
  hideMobile,
  hideDesktop,
}) => {
  const isTicker = type === "ticker" && logos?.length > 6
  const renderTicker = () => {
    return (
      <LogoTickerWrapper>
        <TickerContent>
          <LogoItems>
            {logos.map((logo, index) => (
              <LogoItem key={logo._key + "-0"}>
                <div>
                  <Image image={logo?.asset?.gatsbyImageData} loading="eager" />
                </div>
              </LogoItem>
            ))}
          </LogoItems>
          <LogoItems>
            {logos.map((logo, index) => (
              <LogoItem key={logo._key + "-1"}>
                <div>
                  <Image image={logo?.asset?.gatsbyImageData} loading="eager" />
                </div>
              </LogoItem>
            ))}
          </LogoItems>
          <LogoItems>
            {logos.map((logo, index) => (
              <LogoItem key={logo._key + "-2"}>
                <div>
                  <Image image={logo?.asset?.gatsbyImageData} loading="eager" />
                </div>
              </LogoItem>
            ))}
          </LogoItems>
        </TickerContent>
      </LogoTickerWrapper>
    )
  }

  return (
    <Wrapper
      className={className}
      prevTheme={prevTheme}
      setTheme={theme}
      nextTheme={nextTheme}
      isFirstSection={isFirstSection}
      id={id}
      fullWidth={isTicker || layoutSize === "fullWidth"}
      padded={layoutSize === "fullWidth" ? false : true}
      layoutSize={layoutSize}
      alignment={alignment || "center"}
      vertical={spacing?.vertical}
      horizontal={spacing?.horizontal}
      hideMobile={hideMobile}
      hideDesktop={hideDesktop}
    >
      <Inner>
        {headline && (
          <Container medium={isTicker ? "m 1 [12] 1 m" : "1 [12] 1"}>
            <Headline
              headlineSize={headingSize ? headingSize : headlineSize}
              className={headingSize ? headingSize : headlineSize}
            >
              {headline}
            </Headline>
          </Container>
        )}

        {isTicker ? (
          renderTicker()
        ) : (
          <ResponsiveComponent
            small={renderTicker()}
            large={
              <Grid
                small="m [1] m"
                medium={isTicker ? "m 1 [12] 1 m" : "1 [12] 1"}
                css={css`
                  overflow: hidden;
                `}
              >
                <LogoGrid alignment={alignment}>
                  {logos.map((logo, index) => {
                    if (!logo?.asset?.gatsbyImageData) {
                      return false
                    }
                    return (
                      <LogoItem key={logo?._key + "-0"}>
                        <div>
                          <Image
                            image={logo?.asset?.gatsbyImageData}
                            loading="eager"
                          />
                        </div>
                      </LogoItem>
                    )
                  })}
                </LogoGrid>
              </Grid>
            }
          />
        )}
      </Inner>
    </Wrapper>
  )
}

export default LogoTicker
